import tw, { css, theme } from 'twin.macro'
import { Toaster as BaseToaster } from 'react-hot-toast'
import { AnimatePresence, motion } from 'framer-motion'

import type { ToasterProps, ToastProps } from './types'

export type { TypeToast } from './types'

export function Toaster({
  position = 'bottom-center',
  reverseOrder = true,
  ...props
}: ToasterProps) {
  return (
    <BaseToaster
      position={position}
      reverseOrder={reverseOrder}
      /**
       * The default value is `position: fixed` which use the center
       * of the viewport.
       *
       * We need to center the toast based on another element (like the map
       * or the main content of the Overview UI). For that we need to use
       * `position: sticky`.
       *
       * See https://stackoverflow.com/questions/5209814/can-i-position-an-element-fixed-relative-to-parent
       */
      containerStyle={{ position: 'sticky' }}
      {...props}
    />
  )
}

const toastMaxWidth = css`
  max-width: 400px;

  @media (min-width: ${theme`screens.sm`}) {
    min-width: 400px;
  }
`

const variants = {
  initial: {
    opacity: 0.5,
    scale: 0.6,
    y: '200%',
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    scale: 1,
    y: '100%',
  },
} as const

export const StaticToast = ({
  icon,
  title,
  subtitle,
  action,
}: Omit<ToastProps, 't'>) => (
  <div
    css={[
      toastMaxWidth,
      tw`flex items-center p-4 gap-4 bg-default-subdued rounded-lg border border-default-muted shadow-2xl`,
    ]}
  >
    <div tw="flex-shrink-0">{icon}</div>

    <div tw="space-y-1 flex-grow">
      <div tw="font-brand text-sm-brand font-medium text-default-emphasis">
        {title}
      </div>
      {subtitle && (
        <div tw="font-brand text-xs-brand font-normal text-default-muted">
          {subtitle}
        </div>
      )}
    </div>

    <div tw="flex-shrink-0 font-brand text-sm-brand font-medium text-brand-neutral">
      {action}
    </div>
  </div>
)

export function DefaultToast({ t, icon, title, subtitle, action }: ToastProps) {
  return (
    <AnimatePresence>
      {t.visible && (
        <motion.div
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <StaticToast
            icon={icon}
            title={title}
            subtitle={subtitle}
            action={action}
          />
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export { toast, useToasterStore, useToaster } from 'react-hot-toast'
